import React, { useState, useEffect, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  Typography, Button, Box, Alert, CircularProgress, Stepper, Step, StepLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Checkbox, Chip, Select, MenuItem, FormControl, Tooltip, useMediaQuery, useTheme,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ModalWrapper from '../components/ModalWrapper';
import { apiFetch } from '../apiFetch';
import { ReceiptMatchResult, ReceiptScanResponse, ReceiptGroceryItem, ReceiptDebugInfo } from '../types/ReceiptScan';
import { isVirtualCamera } from '../utils/camera';

interface ReceiptScanModalProps {
  open: boolean;
  onClose: () => void;
  onPricesUpdated: () => void;
  shopId: number;
  groceryItems: ReceiptGroceryItem[];
  currentPrices: Map<number, string>;
}

interface CameraDevice {
  deviceId: string;
  label: string;
}

interface TokenPayload {
  is_superuser?: boolean;
}

const CAMERA_STORAGE_KEY = 'receiptScanCameraId';

const ReceiptScanModal: React.FC<ReceiptScanModalProps> = ({
  open,
  onClose,
  onPricesUpdated,
  shopId,
  groceryItems,
  currentPrices,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiUrl = process.env.REACT_APP_API_URL;

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Camera state
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);

  // Zoom state
  const [maxZoom, setMaxZoom] = useState<number>(1);
  const [currentZoom, setCurrentZoom] = useState<number>(1);
  const [availableZoomLevels, setAvailableZoomLevels] = useState<number[]>([1]);

  // Results state
  const [matches, setMatches] = useState<ReceiptMatchResult[]>([]);
  const [selectedUpdates, setSelectedUpdates] = useState<Set<number>>(new Set());
  const [multipleMatchSelections, setMultipleMatchSelections] = useState<Map<number, number>>(new Map());
  const [updating, setUpdating] = useState(false);
  const [processingReceipt, setProcessingReceipt] = useState(false);

  // Debug state
  const [debugInfo, setDebugInfo] = useState<ReceiptDebugInfo | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const steps = ['Introduction', 'Capture Receipt', 'Review Results'];

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    videoTrackRef.current = null;
    setCameraReady(false);
  }, []);

  const resetState = () => {
    setActiveStep(0);
    setError(null);
    setWarning(null);
    setCameras([]);
    setSelectedCamera('');
    setCameraLoading(false);
    setCameraReady(false);
    setMaxZoom(1);
    setCurrentZoom(1);
    setAvailableZoomLevels([1]);
    setMatches([]);
    setSelectedUpdates(new Set());
    setMultipleMatchSelections(new Map());
    setUpdating(false);
    setProcessingReceipt(false);
    setDebugInfo(null);
    setShowDebug(false);
  };

  // Check superuser status
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const payload = jwtDecode<TokenPayload>(token);
        setIsSuperuser(payload.is_superuser === true);
      } catch (err) {
        console.error('Failed to decode token:', err);
      }
    }
  }, []);

  // Cleanup camera on close
  useEffect(() => {
    if (!open) {
      stopCamera();
      resetState();
    }
  }, [open, stopCamera]);

  const loadCameras = useCallback(async () => {
    try {
      // Request permission first
      const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
      tempStream.getTracks().forEach(track => track.stop());
      await new Promise(resolve => setTimeout(resolve, 200));

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices
        .filter(d => d.kind === 'videoinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));

      // Filter to back cameras if available
      const backOnly = videoDevices.filter(d => {
        const label = d.label.toLowerCase();
        if (label.includes('front') || label.includes('user') || label.includes('facetime')) return false;
        if (isVirtualCamera(label)) return false;
        return true;
      });

      const camerasToUse = backOnly.length > 0 ? backOnly : videoDevices;
      setCameras(camerasToUse);

      if (camerasToUse.length > 0) {
        const savedCameraId = localStorage.getItem(CAMERA_STORAGE_KEY);
        const savedExists = savedCameraId && camerasToUse.some(c => c.deviceId === savedCameraId);
        if (savedExists) {
          setSelectedCamera(savedCameraId!);
        } else {
          setSelectedCamera(camerasToUse[0].deviceId);
        }
      }
    } catch (err) {
      console.error('Failed to load cameras:', err);
      setError('Failed to access camera. Please ensure camera permissions are granted.');
    }
  }, []);

  const startCamera = useCallback(async (deviceId: string) => {
    if (!deviceId) return;

    setCameraLoading(true);
    setError(null);

    try {
      stopCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { min: 640, ideal: 1920, max: 2560 },
          height: { min: 480, ideal: 1080, max: 1920 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.muted = true;

        await new Promise<void>((resolve, reject) => {
          const video = videoRef.current!;
          const timeoutId = setTimeout(() => resolve(), 3000);

          const onCanPlay = () => {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', onCanPlay);
            resolve();
          };

          video.addEventListener('canplay', onCanPlay);

          if (video.readyState >= 3) {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', onCanPlay);
            resolve();
          }
        });

        await videoRef.current.play();

        // Configure zoom
        const track = stream.getVideoTracks()[0];
        if (track) {
          videoTrackRef.current = track;
          try {
            const capabilities = track.getCapabilities() as any;
            if (capabilities?.zoom) {
              const max = capabilities.zoom.max || 1;
              setMaxZoom(max);
              const levels = [1, 2, 3, 4].filter(z => z <= max);
              setAvailableZoomLevels(levels.length > 0 ? levels : [1]);
            } else {
              setMaxZoom(1);
              setAvailableZoomLevels([1]);
            }
          } catch (e) {
            setMaxZoom(1);
            setAvailableZoomLevels([1]);
          }
        }

        setCameraReady(true);

        // Save selection
        try {
          localStorage.setItem(CAMERA_STORAGE_KEY, deviceId);
        } catch (e) { /* ignore */ }
      }
    } catch (err: any) {
      console.error('Camera error:', err);
      let errorMessage = 'Camera error. Please try again.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera access.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found. Please connect a camera.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is in use or not available.';
      }

      setError(errorMessage);
    } finally {
      setCameraLoading(false);
    }
  }, [stopCamera]);

  // Start camera when entering capture step
  useEffect(() => {
    if (activeStep === 1 && cameras.length === 0) {
      loadCameras();
    }
  }, [activeStep, cameras.length, loadCameras]);

  // Start camera when camera is selected
  useEffect(() => {
    if (activeStep === 1 && selectedCamera && !cameraReady && !cameraLoading) {
      startCamera(selectedCamera);
    }
  }, [activeStep, selectedCamera, cameraReady, cameraLoading, startCamera]);

  const handleCameraChange = async (deviceId: string) => {
    setSelectedCamera(deviceId);
    await startCamera(deviceId);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !cameraReady) return;

    // Create canvas if needed
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Set canvas size to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to capture image'));
          }
        },
        'image/jpeg',
        0.95
      );
    });
  };

  const handleCapture = async () => {
    setError(null);

    try {
      const imageBlob = await capturePhoto();
      if (!imageBlob) {
        setError('Failed to capture photo. Please try again.');
        return;
      }

      // Stop camera and move to results step with processing spinner
      stopCamera();
      setProcessingReceipt(true);
      setActiveStep(2); // Review Results step

      // Prepare form data
      const formData = new FormData();
      formData.append('image', imageBlob, 'receipt.jpg');
      formData.append('shop_id', shopId.toString());
      formData.append('grocery_items', JSON.stringify(groceryItems));

      // Send to backend
      const response: ReceiptScanResponse = await apiFetch(`${apiUrl}/api/receipts/scan/`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type - let browser set it with boundary for multipart
      });

      if (response.warning) {
        setWarning(response.warning);
      }

      setMatches(response.matches);
      if (response.debug) {
        setDebugInfo(response.debug);
      }
      setProcessingReceipt(false);
    } catch (err: any) {
      console.error('Receipt scan error:', err);
      setError(err.message || 'Failed to process receipt. Please try again.');
      setProcessingReceipt(false);
      // Stay on results step to show error message clearly
      // User can click "Try scanning again" to go back to camera
    }
  };

  const handleCheckboxChange = (groceryId: number, checked: boolean) => {
    setSelectedUpdates(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(groceryId);
      } else {
        next.delete(groceryId);
      }
      return next;
    });
  };

  const handleMultipleMatchSelection = (groceryId: number, matchIndex: number) => {
    setMultipleMatchSelections(prev => {
      const next = new Map(prev);
      next.set(groceryId, matchIndex);
      return next;
    });
  };

  const handleUpdatePrices = async () => {
    if (selectedUpdates.size === 0) {
      onClose();
      return;
    }

    setUpdating(true);
    setError(null);

    try {
      for (const groceryId of Array.from(selectedUpdates)) {
        const match = matches.find(m => m.grocery_id === groceryId);
        if (!match) continue;

        // For multiple matches, get the selected one
        let price = match.extracted_price;
        let isDiscounted = match.is_discounted;
        let priceBeforeDiscount = match.price_before_discount;

        if (match.status === 'multiple_matches' && match.possible_matches.length > 0) {
          const selectedIdx = multipleMatchSelections.get(groceryId) ?? 0;
          const selectedMatch = match.possible_matches[selectedIdx];
          if (selectedMatch) {
            price = selectedMatch.price;
            isDiscounted = selectedMatch.is_discounted;
            priceBeforeDiscount = selectedMatch.price_before_discount;
          }
        }

        // Create price record
        await apiFetch(`${apiUrl}/api/prices/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shop: shopId,
            grocery: groceryId,
            price: price,
            is_discounted: isDiscounted,
            price_before_discount: priceBeforeDiscount,
          }),
        });
      }

      onPricesUpdated();
      onClose();
    } catch (err: any) {
      console.error('Price update error:', err);
      setError(err.message || 'Failed to update some prices. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleZoomChange = async (_: React.MouseEvent<HTMLElement>, newZoom: number | null) => {
    if (newZoom === null || !videoTrackRef.current) return;
    try {
      const actualZoom = Math.min(newZoom, maxZoom);
      await videoTrackRef.current.applyConstraints({ advanced: [{ zoom: actualZoom } as any] });
      setCurrentZoom(actualZoom);
    } catch (e) {
      console.error('Failed to apply zoom:', e);
    }
  };

  const getStatusLabel = (match: ReceiptMatchResult) => {
    switch (match.status) {
      case 'no_change':
        return <Chip label="No change needed" size="small" color="default" />;
      case 'not_found':
        return <Chip label="Not found" size="small" color="warning" variant="outlined" />;
      case 'matched':
        return <Chip label="Match found" size="small" color="success" variant="outlined" />;
      case 'multiple_matches':
        return <Chip label="Select match" size="small" color="info" variant="outlined" />;
      default:
        return null;
    }
  };

  const renderIntroStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body1">
        After your shopping trip, if your grocery list requires price updates you can scan your paper receipt to look for new prices for the items in your grocery list.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button variant="contained" onClick={handleNext}>
          Start Scanning
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        How it works:
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 3 }}>
        <Typography component="li" variant="body2" color="text.secondary">
          Take a clear photo of the items on your receipt
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary">
          We'll extract prices and match them to your shopping list items
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary">
          Review the matches and select which prices to update
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Tips for best results:
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 3 }}>
        <Typography component="li" variant="body2" color="text.secondary">
          Ensure good lighting and hold the camera steady
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary">
          Use zoom to get a clear view of item names and prices
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary">
          Flatten the receipt to avoid creases and shadows
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary">
          Avoid crumpling the receipt - wrinkles cause parsing errors
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary">
          Fold the receipt so only the grocery items are visible in the photo
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
        Note: You have 3 scan attempts per 15 minutes.
      </Typography>
    </Box>
  );

  const renderCaptureStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {cameras.length > 1 && (
        <FormControl fullWidth size="small">
          <Select
            value={selectedCamera}
            onChange={(e) => handleCameraChange(e.target.value)}
            displayEmpty
          >
            {cameras.map((cam) => (
              <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'grey.900',
          minHeight: 300,
        }}
      >
        {cameraLoading && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CircularProgress />
          </Box>
        )}

        <video
          ref={videoRef}
          style={{
            display: cameraReady ? 'block' : 'none',
            width: '100%',
            maxHeight: isMobile ? 350 : 400,
            objectFit: 'cover',
          }}
          playsInline
          muted
        />

        {/* Zoom controls */}
        {cameraReady && availableZoomLevels.length > 1 && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <ToggleButtonGroup
              value={currentZoom}
              exclusive
              onChange={handleZoomChange}
              size="small"
              sx={{
                bgcolor: 'rgba(0,0,0,0.6)',
                '& .MuiToggleButton-root': {
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  px: 1.5,
                  py: 0.5,
                  minWidth: 40,
                  fontSize: 14,
                  '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                },
              }}
            >
              {availableZoomLevels.map((level) => (
                <ToggleButton key={level} value={level}>{level}x</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Capture button */}
        {cameraReady && (
          <Box sx={{
            position: 'absolute',
            bottom: 16,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
          }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<CameraAltIcon />}
              onClick={handleCapture}
              sx={{ borderRadius: 8 }}
            >
              Capture Receipt
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderResultsStep = () => {
    // Show processing spinner while analyzing receipt
    if (processingReceipt) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          gap: 2,
        }}>
          <CircularProgress size={48} />
          <Typography variant="body1">Analyzing receipt...</Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a few seconds
          </Typography>
        </Box>
      );
    }

    const hasSelectableItems = matches.some(m =>
      m.status === 'matched' || m.status === 'multiple_matches'
    );

    // Check if any items were actually found on the receipt (not just 'not_found')
    const hasFoundItems = matches.some(m =>
      m.status === 'matched' || m.status === 'multiple_matches' || m.status === 'no_change'
    );

    // Determine which message/content to show
    const showTable = hasSelectableItems && !error;
    const noGroceriesFound = !hasFoundItems && !error;
    const allUpToDate = hasFoundItems && !hasSelectableItems && !error;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {warning && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {warning}
          </Alert>
        )}

        {/* Error state */}
        {error && (
          <Typography color="text.secondary">
            Unable to process the receipt. Please try again.
          </Typography>
        )}

        {/* No matches between receipt and shopping list */}
        {noGroceriesFound && (
          <Typography color="text.secondary">
            {debugInfo && debugInfo.parsed_items.length > 0
              ? `The scan found ${debugInfo.parsed_items.length} item${debugInfo.parsed_items.length !== 1 ? 's' : ''} on the receipt, but none matched your shopping list.`
              : "The scan couldn't read any items from the receipt. Make sure the receipt is clearly visible and try again."}
          </Typography>
        )}

        {/* Groceries found but all prices are current */}
        {allUpToDate && (
          <Typography color="text.secondary">
            Good news! All the prices in your shopping list are already up to date with this receipt.
          </Typography>
        )}

        {/* Show table only when there are items to review */}
        {showTable && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 50 }}></TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">New Price</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matches.map((match) => {
                  const isSelectable = match.status === 'matched' || match.status === 'multiple_matches';
                  const isChecked = selectedUpdates.has(match.grocery_id);
                  const currentPrice = currentPrices.get(match.grocery_id);

                  // Get selected match for multiple matches
                  let displayPrice = match.extracted_price;
                  let displayReceiptName = match.receipt_item_name;
                  if (match.status === 'multiple_matches' && match.possible_matches.length > 0) {
                    const selectedIdx = multipleMatchSelections.get(match.grocery_id) ?? 0;
                    const selectedMatch = match.possible_matches[selectedIdx];
                    if (selectedMatch) {
                      displayPrice = selectedMatch.price;
                      displayReceiptName = selectedMatch.receipt_name;
                    }
                  }

                  return (
                    <TableRow
                      key={match.grocery_id}
                      sx={{
                        opacity: isSelectable ? 1 : 0.6,
                        bgcolor: isChecked ? 'action.selected' : 'inherit',
                      }}
                    >
                      <TableCell padding="checkbox">
                        {isSelectable && (
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => handleCheckboxChange(match.grocery_id, e.target.checked)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {match.grocery_name}
                        </Typography>
                        {displayReceiptName && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Receipt: {displayReceiptName}
                          </Typography>
                        )}
                        {match.quantity_mismatch && (
                          <Tooltip title="Quantity on receipt may differ from shopping list">
                            <Chip
                              icon={<WarningIcon />}
                              label="Qty mismatch"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {currentPrice ? `$${currentPrice}` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {match.status === 'multiple_matches' && match.possible_matches.length > 0 ? (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={multipleMatchSelections.get(match.grocery_id) ?? 0}
                              onChange={(e) => handleMultipleMatchSelection(match.grocery_id, e.target.value as number)}
                            >
                              {match.possible_matches.map((pm, idx) => (
                                <MenuItem key={idx} value={idx}>
                                  ${pm.price} ({Math.round(pm.score * 100)}%)
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : displayPrice ? (
                          <Box>
                            <Typography variant="body2" fontWeight="medium" color={match.is_discounted ? 'success.main' : 'inherit'}>
                              ${displayPrice}
                            </Typography>
                            {match.is_discounted && match.price_before_discount && (
                              <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                ${match.price_before_discount}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusLabel(match)}
                        {match.status === 'matched' && match.confidence < 0.85 && (
                          <Tooltip title={`Confidence: ${Math.round(match.confidence * 100)}%`}>
                            <HelpOutlineIcon sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary', verticalAlign: 'middle' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {showTable && (
          <Typography variant="body2" color="text.secondary">
            Select items to update, then click "Update Prices".
          </Typography>
        )}

        {/* Try again option */}
        {!processingReceipt && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <Box
              component="span"
              onClick={handleTryAgain}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Try scanning again
            </Box>
            {' '}for better results (3 attempts per 15 minutes).
          </Typography>
        )}

        {/* Debug section - only visible to superusers */}
        {debugInfo && isSuperuser && !processingReceipt && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowDebug(!showDebug)}
              sx={{ mb: 1 }}
            >
              {showDebug ? 'Hide Debug Info' : 'Show Debug Info'}
            </Button>

            {showDebug && (
              <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.100', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
                {/* First Pass Info */}
                {debugInfo.first_pass_info && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      First Pass (Format Detection):
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        mb: 2,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        fontSize: 12,
                      }}
                    >
                      <div><strong>Total Lines:</strong> {debugInfo.first_pass_info.total_lines}</div>
                      <div><strong>Lines with $:</strong> {debugInfo.first_pass_info.lines_with_dollar}</div>
                      <div><strong>Lines without $:</strong> {debugInfo.first_pass_info.lines_without_dollar}</div>
                      <div>
                        <strong>Items use $:</strong>{' '}
                        <span style={{ color: debugInfo.first_pass_info.items_use_dollar ? 'green' : 'blue' }}>
                          {debugInfo.first_pass_info.items_use_dollar ? 'Yes' : 'No (Country Grocer format)'}
                        </span>
                      </div>
                    </Box>

                    {/* Line Parse Attempts */}
                    {debugInfo.first_pass_info.line_parse_attempts && debugInfo.first_pass_info.line_parse_attempts.length > 0 && (
                      <>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                          Line Parse Attempts ({debugInfo.first_pass_info.line_parse_attempts.length}):
                        </Typography>
                        <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                          {debugInfo.first_pass_info.line_parse_attempts.map((attempt, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                p: 0.5,
                                mb: 0.5,
                                bgcolor: attempt.result === 'success' ? 'success.light' : attempt.result === 'skipped' ? 'grey.100' : 'grey.200',
                                borderRadius: 1,
                                fontSize: 11,
                                fontFamily: 'monospace',
                                opacity: attempt.result === 'skipped' ? 0.6 : 1,
                              }}
                            >
                              <div>
                                <strong style={{ color: attempt.result === 'success' ? 'green' : attempt.result === 'skipped' ? 'grey' : 'red' }}>
                                  {attempt.result === 'success' ? '✓' : attempt.result === 'skipped' ? '○' : '✗'}
                                </strong>{' '}
                                {attempt.line}
                              </div>
                              {attempt.result === 'success' ? (
                                <div style={{ color: 'green', marginLeft: 16 }}>
                                  → {attempt.name} @ ${attempt.price}
                                </div>
                              ) : (
                                <div style={{ color: '#666', marginLeft: 16 }}>
                                  → {attempt.reason}
                                </div>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}
                  </>
                )}

                <Typography variant="subtitle2" gutterBottom>
                  Raw OCR Text:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    bgcolor: 'white',
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    mb: 2,
                  }}
                >
                  {debugInfo.raw_ocr_text || '(empty)'}
                </Box>

                <Typography variant="subtitle2" gutterBottom>
                  Parsed Items ({debugInfo.parsed_items.length}):
                </Typography>
                {debugInfo.parsed_items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No items were parsed from the receipt.
                  </Typography>
                ) : (
                  <Box sx={{ fontSize: 12 }}>
                    {debugInfo.parsed_items.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1,
                          mb: 0.5,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.300',
                        }}
                      >
                        <strong>{item.name}</strong> - ${item.unit_price}
                        {item.quantity > 1 && ` (qty: ${item.quantity})`}
                        {item.is_discounted && ` [SALE from $${item.price_before_discount}]`}
                        <Typography variant="caption" display="block" color="text.secondary">
                          Raw: {item.raw_text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderIntroStep();
      case 1:
        return renderCaptureStep();
      case 2:
        return renderResultsStep();
      default:
        return null;
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      stopCamera();
    }
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Move to camera step
      setActiveStep(1);
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const handleTryAgain = () => {
    // Reset results and go back to capture step
    setMatches([]);
    setSelectedUpdates(new Set());
    setMultipleMatchSelections(new Map());
    setDebugInfo(null);
    setShowDebug(false);
    setWarning(null);
    setError(null);
    setActiveStep(1); // Go to capture step
  };

  return (
    <ModalWrapper open={open} onClose={handleClose}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">
          Scan Receipt
        </Typography>
        <Tooltip
          title="This feature is experimental. Accuracy depends on receipt quality, phone camera, lighting conditions, and store receipt layout. We're continuously improving the scanning algorithms."
          arrow
        >
          <Chip label="Experimental" size="small" color="warning" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Box>

      <Stepper activeStep={activeStep} sx={{ my: 2 }} alternativeLabel={isMobile}>
        {steps.map((label, index) => (
          <Step key={label} completed={activeStep > index}>
            <StepLabel>{!isMobile && label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {renderStepContent()}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        {activeStep === 0 ? (
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
        ) : activeStep === 2 && processingReceipt ? (
          <Box /> // Empty placeholder during processing
        ) : activeStep === 2 ? (
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <Button variant="outlined" onClick={handleBack}>
            Back
          </Button>
        )}

        {/* Start Scanning button moved into intro step content */}

        {activeStep === 2 && !processingReceipt && selectedUpdates.size > 0 && (
          <Button
            variant="contained"
            onClick={handleUpdatePrices}
            disabled={updating}
            startIcon={updating ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            Update {selectedUpdates.size} Price{selectedUpdates.size !== 1 ? 's' : ''}
          </Button>
        )}
      </Box>
    </ModalWrapper>
  );
};

export default ReceiptScanModal;
